import React from 'react';
import { clsx } from 'clsx';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'sm' | 'md';
  color?: string; // Custom color from OpenProject
  className?: string;
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  color,
  className,
}: BadgeProps) {
  const variantStyles = {
    default: 'bg-gray-100 text-gray-700',
    primary: 'bg-primary-100 text-primary-700',
    success: 'bg-success-100 text-success-600',
    warning: 'bg-warning-100 text-warning-600',
    danger: 'bg-danger-100 text-danger-600',
    info: 'bg-blue-100 text-blue-700',
  };

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
  };

  // Dacă avem o culoare custom de la OpenProject
  const customStyle = color
    ? {
        backgroundColor: `${color}20`,
        color: color,
        borderColor: color,
      }
    : undefined;

  return (
    <span
      className={clsx(
        'inline-flex items-center font-medium rounded-full',
        !color && variantStyles[variant],
        sizeStyles[size],
        color && 'border',
        className
      )}
      style={customStyle}
    >
      {children}
    </span>
  );
}

// Badge pentru status cu culoare din OpenProject
interface StatusBadgeProps {
  name: string;
  color?: string;
  isClosed?: boolean;
}

export function StatusBadge({ name, color, isClosed }: StatusBadgeProps) {
  const defaultColor = isClosed ? '#22c55e' : '#3b82f6';
  const badgeColor = color || defaultColor;

  return (
    <Badge color={badgeColor} size="sm">
      {name}
    </Badge>
  );
}

// Badge pentru prioritate
interface PriorityBadgeProps {
  name: string;
  color?: string;
}

export function PriorityBadge({ name, color }: PriorityBadgeProps) {
  // Culori implicite pentru priorități cunoscute
  const getPriorityColor = (priorityName: string): string => {
    const lowerName = priorityName.toLowerCase();
    if (lowerName.includes('immediate') || lowerName.includes('imediat')) {
      return '#dc2626';
    }
    if (lowerName.includes('high') || lowerName.includes('înalt')) {
      return '#f59e0b';
    }
    if (lowerName.includes('low') || lowerName.includes('scăzut')) {
      return '#6b7280';
    }
    return '#3b82f6'; // Normal
  };

  const badgeColor = color || getPriorityColor(name);

  return (
    <Badge color={badgeColor} size="sm">
      {name}
    </Badge>
  );
}

// Badge pentru tip de work package
interface TypeBadgeProps {
  name: string;
  color?: string;
  isMilestone?: boolean;
}

export function TypeBadge({ name, color, isMilestone }: TypeBadgeProps) {
  const defaultColor = isMilestone ? '#8b5cf6' : '#0ea5e9';
  const badgeColor = color || defaultColor;

  return (
    <Badge color={badgeColor} size="sm">
      {name}
    </Badge>
  );
}
